import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Heart, Globe, Flame } from 'lucide-react';
import SiteNav from '../components/SiteNav';
import FadeImage from '../components/FadeImage';
import { contentApi } from '../lib/api';

const DEFAULTS = {
  hero_title: 'About The Kalari Movement',
  hero_subtitle: 'We exist to carry one of the world\'s oldest martial traditions into the hands of every sincere practitioner — across borders, languages, and backgrounds.',
  story_heading: 'How We Began',
  story_body: 'The Kalari Movement was founded by practitioners who trained for years under traditional Kalaripayattu masters in Kerala. We witnessed first-hand how this extraordinary system — 3,000 years old, a source of healing, strength, and philosophy — was invisible to most of the world simply because of geography.\n\nWe set out to change that. Not by watering it down or making it a fitness trend, but by transmitting it with the same rigour and reverence that has sustained it through centuries. Every class, every curriculum day, every live session carries that intention.',
  story_image_url: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800&q=80',
  founder_name: 'The Founder',
  founder_title: 'Founder & Head Instructor',
  founder_bio: 'Trained under traditional Kalaripayattu masters in Kerala for over a decade, the founder of The Kalari Movement has dedicated their life to preserving and transmitting this ancient martial art. With deep roots in the lineage and a vision to make the practice accessible worldwide, they created this programme to bridge tradition and modern life.',
  founder_image_url: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&q=80',
  value_1_title: 'Authenticity',
  value_1_body: 'Every technique, sequence, and principle we teach traces back to living masters and unbroken lineage.',
  value_2_title: 'Rigour',
  value_2_body: 'We do not simplify or soften the practice. We meet students where they are and build them toward mastery.',
  value_3_title: 'Community',
  value_3_body: 'Our practitioners form a global sangha — connected by a shared commitment to the art and to each other.',
  value_4_title: 'Accessibility',
  value_4_body: 'Geography should never be the reason someone cannot access this tradition. We remove that barrier.',
};

const VALUE_ICONS = [Shield, Heart, Globe, Flame];

export default function AboutPage() {
  const [content, setContent] = useState(null);

  useEffect(() => {
    contentApi.getAboutContent()
      .then(r => setContent(r.data && Object.keys(r.data).length ? { ...DEFAULTS, ...r.data } : DEFAULTS))
      .catch(() => setContent(DEFAULTS));
  }, []);

  if (!content) return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SiteNav />
      <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 80px)' }}>
        <div className="w-8 h-8 border-2 border-[#9A3B26] border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SiteNav />

      {/* Hero */}
      <section className="bg-[#1C1917] pt-32 pb-24 px-6 md:px-12 lg:px-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15"
          style={{ backgroundImage: 'radial-gradient(circle at 15% 60%, #9A3B26 0%, transparent 50%), radial-gradient(circle at 85% 20%, #DDA15E 0%, transparent 40%)' }}
        />
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 border border-[#DDA15E]/40 text-[#DDA15E] rounded-full px-4 py-1.5 text-xs font-semibold tracking-widest uppercase mb-8">
            <Flame className="h-3.5 w-3.5" /> Our Story
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {content.hero_title}
          </h1>
          <p className="text-lg text-white/65 max-w-2xl mx-auto leading-relaxed">
            {content.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1 space-y-6">
            <p className="text-[#9A3B26] font-semibold text-sm uppercase tracking-widest">Our Story</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#292524] leading-tight">
              {content.story_heading}
            </h2>
            {content.story_body.split('\n\n').map((para, i) => (
              <p key={i} className="text-[#78716C] leading-relaxed">{para}</p>
            ))}
          </div>
          <div className="order-1 lg:order-2">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-[#E7DDD5]">
              <FadeImage src={content.story_image_url} alt="The Kalari Movement" className="w-full h-full object-cover" lqip={content.story_image_lqip} />
            </div>
          </div>
        </div>
      </section>

      {/* Founder */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#F4EFE6]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="flex justify-center">
            <div className="w-full max-w-sm aspect-[3/4] rounded-2xl overflow-hidden bg-[#E7DDD5] shadow-xl border border-[#DDA15E]/30">
              <FadeImage src={content.founder_image_url} alt={content.founder_name} className="w-full h-full object-cover object-top" lqip={content.founder_image_lqip} />
            </div>
          </div>
          <div className="space-y-5">
            <p className="text-[#9A3B26] font-semibold text-sm uppercase tracking-widest">About the Founder</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#292524] leading-tight">
              {content.founder_name}
            </h2>
            <p className="text-[#DDA15E] font-medium text-base">{content.founder_title}</p>
            {content.founder_bio.split('\n\n').map((para, i) => (
              <p key={i} className="text-[#78716C] leading-relaxed">{para}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#9A3B26] font-semibold text-sm uppercase tracking-widest mb-3">What We Stand For</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#292524]">Our Values</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((n, i) => {
              const Icon = VALUE_ICONS[i];
              return (
                <div key={n} className="bg-white rounded-2xl p-7 border border-[#E7E5E4]">
                  <div className="w-11 h-11 rounded-xl bg-[#F4EFE6] flex items-center justify-center mb-5">
                    <Icon className="h-5 w-5 text-[#9A3B26]" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-[#292524] mb-2">
                    {content[`value_${n}_title`]}
                  </h3>
                  <p className="text-[#78716C] text-sm leading-relaxed">
                    {content[`value_${n}_body`]}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 md:px-12 bg-[#1C1917]">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white">Ready to Begin?</h2>
          <p className="text-white/60 text-lg">Join the next 21-day batch and experience the tradition first-hand.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/programme" className="inline-flex items-center gap-2 bg-[#9A3B26] hover:bg-[#7A2E1E] text-white font-medium px-8 py-4 rounded-full transition-all duration-300 shadow-lg">
              View the Programme <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-white/25 hover:border-white/60 text-white font-medium px-8 py-4 rounded-full transition-all duration-300">
              Get in Touch
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#141210] text-white/50 py-10 px-6 md:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <span className="font-heading font-bold text-white text-base">The Kalari Movement</span>
          <div className="flex gap-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/about" className="hover:text-white transition-colors">About</Link>
            <Link to="/programme" className="hover:text-white transition-colors">Programme</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
          </div>
          <span>© {new Date().getFullYear()} The Kalari Movement. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
